import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight } from "lucide-react";
import { HRMark, AurixMark } from "@/components/site/Logo";

/**
 * Custom animated modal — uses framer-motion instead of Radix Dialog so we
 * can control the premium 3D-tilt entrance and gold glass aesthetic.
 */
const DivisionModal = ({ open, onClose, division, onExplore }) => {
  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!division) return null;
  const Icon = division.icon;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-center justify-center px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          data-testid="division-modal"
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/75 backdrop-blur-md"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          {/* Panel */}
          <motion.div
            role="dialog"
            aria-modal="true"
            initial={{ opacity: 0, y: 40, rotateX: -8, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, rotateX: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, rotateX: -4, scale: 0.96 }}
            transition={{ duration: 0.45, ease: [0.2, 0.8, 0.2, 1] }}
            className="relative z-10 w-full max-w-xl overflow-hidden rounded-3xl border border-white/[0.08] bg-[#0d0d0f]/95 p-8 shadow-[0_40px_120px_-20px_rgba(212,175,55,0.35)] backdrop-blur-xl sm:p-10"
            style={{ transformPerspective: 1200 }}
            data-testid={`division-modal-${division.key}`}
          >
            {/* Gold gradient rim */}
            <div
              className="pointer-events-none absolute inset-0 rounded-3xl"
              style={{
                background:
                  "linear-gradient(135deg, rgba(212,175,55,0.18) 0%, transparent 40%, transparent 60%, rgba(212,175,55,0.08) 100%)",
                mask: "linear-gradient(#000,#000) content-box, linear-gradient(#000,#000)",
                WebkitMask:
                  "linear-gradient(#000,#000) content-box, linear-gradient(#000,#000)",
                padding: 1,
                WebkitMaskComposite: "xor",
                maskComposite: "exclude",
              }}
            />
            {/* Floating aura */}
            <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.32),transparent_60%)] blur-2xl" />

            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              data-testid="division-modal-close"
              className="absolute right-5 top-5 grid h-9 w-9 place-items-center rounded-full border border-white/10 bg-white/[0.04] text-zinc-300 transition-colors duration-300 hover:border-[var(--gold)]/50 hover:text-[var(--gold-bright)]"
            >
              <X className="h-4 w-4" />
            </button>

            {/* Header row: division logo + parent HR mark */}
            <div className="mb-8 flex items-center gap-4">
              <div className="grid h-14 w-14 place-items-center rounded-2xl border border-[var(--gold)]/40 bg-[var(--gold)]/10 text-[var(--gold-bright)]">
                {division.isFlagship ? (
                  <AurixMark size={44} className="rounded-lg" />
                ) : (
                  <Icon className="h-7 w-7" />
                )}
              </div>
              <div className="flex items-center gap-2 text-[11px] text-zinc-500">
                <HRMark size={22} className="rounded-full" />
                <span className="font-mono-label">Division of HR ENTERPRISES</span>
              </div>
            </div>

            <div className="font-mono-label mb-3 text-[var(--gold)]">
              {division.tag}
            </div>
            <h3 className="font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">
              {division.name}
            </h3>
            <p className="mt-3 font-serif-display text-[19px] italic text-zinc-300">
              {division.hook}
            </p>
            <p className="mt-5 text-[14.5px] leading-relaxed text-zinc-400">
              {division.long}
            </p>

            <ul className="mt-6 grid grid-cols-2 gap-x-4 gap-y-2 text-[13px] text-zinc-300">
              {division.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2">
                  <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--gold)]" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => {
                  if (onExplore && division.slug) {
                    onExplore(division.slug);
                  } else {
                    onClose();
                  }
                }}
                data-testid={`division-modal-cta-${division.key}`}
                className="btn-gold"
              >
                {division.cta}
                <ArrowUpRight className="h-4 w-4" strokeWidth={2.2} />
              </button>
              <button
                type="button"
                onClick={onClose}
                className="btn-ghost"
              >
                Close
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DivisionModal;
